import ApiService from "../../../services/ApiService";
import { normalizeHomeJigsawGroup } from "./jigsawGroup";
import {
    pickDefaultJigsawPlayTemplate,
    pickJigsawSquareImageUrl,
    pickJigsawTemplateForTileSize,
    pickPrimaryJigsawSeed,
    resolveJigsawTemplateTileSize,
} from "./jigsawScreenshotUtils";

function pickTemplateFromGroup(templates, { jigsawId, expectedGrid }) {
    const list = Array.isArray(templates) ? templates : [];
    if (list.length === 0) return null;

    if (jigsawId) {
        const byId = list.find((t) => t?.id === jigsawId);
        if (byId) return byId;
    }

    if (expectedGrid) {
        const primarySeed = pickPrimaryJigsawSeed(list);
        const byGrid = pickJigsawTemplateForTileSize(list, expectedGrid, primarySeed);
        if (byGrid) return byGrid;
    }

    return pickDefaultJigsawPlayTemplate(list);
}

/**
 * Load story group + template for a screenshot scene.
 * Uses `storyId` (same as `/puzzle/play-jigsaw/:storyId`).
 */
export async function loadScreenshotJigsaw(scene) {
    const { jigsawId, storyId, expectedGrid } = scene;

    if (!storyId) {
        throw new Error("storyId is required for screenshot scenes");
    }

    const raw = await ApiService.getData(`/home/jigsaw/${encodeURIComponent(storyId)}`);
    const group = normalizeHomeJigsawGroup(raw) ?? raw;
    const template = pickTemplateFromGroup(group?.templates, { jigsawId, expectedGrid });
    if (!template) {
        throw new Error(
            expectedGrid
                ? `No ${expectedGrid}×${expectedGrid} jigsaw template on story ${storyId}`
                : `No jigsaw template on story ${storyId}`,
        );
    }
    return { group, template, storyId };
}

async function loadJigsawStoryGroup(storyId) {
    const raw = await ApiService.getData(`/home/jigsaw/${encodeURIComponent(storyId)}`);
    return normalizeHomeJigsawGroup(raw) ?? raw;
}

/**
 * Load story groups for two-column gallery screenshot scenes (cover images only).
 */
export async function loadScreenshotGallery(scene) {
    const prepend = Array.isArray(scene.galleryPrepend) ? scene.galleryPrepend : [];
    const items = [];

    for (const entry of prepend) {
        const storyId = entry?.storyId;
        if (!storyId) continue;
        const group = await loadJigsawStoryGroup(storyId);
        if (!group) continue;
        if (!getImageUrl(group?.story)) continue;
        items.push({ group, storyId });
    }

    if (items.length === 0) {
        const single = await loadScreenshotJigsaw(scene);
        items.push(single);
    }

    return items;
}

function mapHomePackRow(row) {
    if (!row || typeof row !== "object") return null;
    const packId = row.packId ?? row.id ?? null;
    const title = String(row.title || "").trim();
    if (!packId && !title) return null;
    const pictures = Array.isArray(row.pictures)
        ? row.pictures
            .map((pic) => ({
                storyId: pic?.storyId ?? pic?.id ?? null,
                imageUrl: pic?.imageUrl ?? pic?.image_url ?? null,
            }))
            .filter((pic) => pic.imageUrl)
        : [];
    return {
        packId: packId || title,
        title: title || "Pack",
        tier: row.tier || null,
        free: row.free === true || String(row.tier || "").toLowerCase() === "free",
        owned: row.owned === true,
        priceUsd: row.priceUsd ?? row.price_usd ?? null,
        price: row.price || null,
        pictureCount: row.pictureCount ?? row.picture_count ?? pictures.length,
        pictures,
        coverUrl: row.coverUrl ?? row.cover_url ?? pictures[0]?.imageUrl ?? null,
    };
}

/**
 * Live published packs for the Premium packs App Store shot.
 * Falls back to mosaic packs built from gallery story images when the API is empty.
 */
export async function loadScreenshotPremiumPacks(scene) {
    try {
        const raw = await ApiService.getData("/home/jigsaw/packs");
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.content) ? raw.content : [];
        const packs = list.map(mapHomePackRow).filter(Boolean);
        if (packs.length > 0) return packs.slice(0, 4);
    } catch {
        /* fall through to gallery mosaics */
    }

    const gallery = await loadScreenshotGallery(scene);
    const images = gallery
        .map((item) => ({
            storyId: item.storyId,
            imageUrl: getImageUrl(item?.group?.story),
            title: getStoryTitle(item?.group?.story),
        }))
        .filter((item) => item.imageUrl);

    const titles = ["Funny", "Animals", "Flowers", "Ocean", "Cities", "Travel"];
    const packs = [];
    for (let i = 0; i < 6; i += 1) {
        const slice = images.slice(i * 2, i * 2 + 4);
        const tiles = slice.length > 0 ? slice : images.slice(0, 4);
        if (tiles.length === 0) break;
        packs.push({
            packId: `shot-pack-${i}`,
            title: titles[i] || tiles[0].title || `Pack ${i + 1}`,
            tier: i === 0 ? "free" : "t2",
            free: i === 0,
            owned: false,
            priceUsd: i === 0 ? 0 : i === 1 ? 0.99 : 1.99,
            pictureCount: Math.max(tiles.length, 4),
            pictures: tiles,
            coverUrl: tiles[0].imageUrl,
        });
    }
    return packs;
}

export function getStoryTitle(story) {
    if (!story) return "Jigsaw puzzle";
    return (
        story.english?.title ??
        story.en?.title ??
        story.french?.title ??
        story.fr?.title ??
        "Jigsaw puzzle"
    );
}

export function getImageUrl(story) {
    return pickJigsawSquareImageUrl(story);
}

export function getTemplateGrid(template) {
    return resolveJigsawTemplateTileSize(template);
}
